<template>
  <div class="subscription-wrapper">
    <!-- Hero Section -->
    <section class="hero-section py-6 mb-6">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6" class="text-center">
            <h1 class="text-h3 mb-4" style="color: #2ab3b8; font-weight: 500">
              Subscribe to Dynamic Insights
            </h1>
            <p class="text-h6 mx-auto" style="color: #37474f; line-height: 1.6">
              Unlock powerful analytics and insights to drive your business forward. Choose the plan
              that's right for you.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Subscription Plans Section -->
    <section class="subscription-section">
      <v-container>
        <!-- Currency Selector -->
        <v-row justify="center" class="mb-4">
          <v-col cols="12" md="10" lg="8">
            <div class="text-center">
              <v-btn-toggle v-model="selectedCurrency" mandatory class="currency-toggle">
                <v-btn value="NZD" class="currency-btn"> 🇳🇿 NZD </v-btn>
                <v-btn value="USD" class="currency-btn"> 🇺🇸 USD </v-btn>
              </v-btn-toggle>
            </div>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <!-- Basic Plan Card -->
            <v-card
              class="subscription-card mb-6"
              :class="{
                'selected-plan': selectedPlan === 'basic',
                'current-plan': currentPlan === 'basic',
              }"
              style="border: 2px solid rgba(42, 179, 184, 0.3)"
              @click="selectPlan('basic')"
            >
              <v-card-text class="pa-6">
                <div class="d-flex justify-space-between align-center mb-4">
                  <div>
                    <h2 class="text-h4" style="color: #2ab3b8; font-weight: 600">
                      Basic Dynamic Insights
                      <v-chip v-if="currentPlan === 'basic'" color="success" small class="ml-2">
                        Current Plan
                      </v-chip>
                    </h2>
                    <p class="text-h6 mt-2" style="color: #37474f">
                      Ideal for trying out the service or non profits
                    </p>
                  </div>
                  <div class="text-right">
                    <div class="text-h3" style="color: #2ab3b8; font-weight: 700">Free</div>
                  </div>
                </div>

                <!-- Features List -->
                <div class="features-list mb-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div
                        class="feature-item mb-3"
                        v-for="feature in basicFeatures.slice(0, 3)"
                        :key="feature"
                      >
                        <v-icon color="#2ab3b8" class="mr-2">mdi-check-circle</v-icon>
                        <span>{{ feature }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div
                        class="feature-item mb-3"
                        v-for="feature in basicFeatures.slice(3)"
                        :key="feature"
                      >
                        <v-icon color="#2ab3b8" class="mr-2">mdi-check-circle</v-icon>
                        <span>{{ feature }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <v-radio-group v-model="selectedPlan" hide-details>
                  <v-radio label="Select Basic Plan" value="basic" color="#2ab3b8"></v-radio>
                </v-radio-group>
              </v-card-text>
            </v-card>

            <!-- Professional Plan Card -->
            <v-card
              class="subscription-card mb-6"
              :class="{
                'selected-plan': selectedPlan === 'professional',
                'current-plan': currentPlan === 'professional',
              }"
              style="border: 2px solid rgba(42, 179, 184, 0.3)"
              @click="selectPlan('professional')"
            >
              <v-card-text class="pa-6">
                <div class="d-flex justify-space-between align-center mb-4">
                  <div>
                    <h2 class="text-h4" style="color: #2ab3b8; font-weight: 600">
                      Professional Dynamic Insights
                      <v-chip
                        v-if="currentPlan === 'professional'"
                        color="success"
                        small
                        class="ml-2"
                      >
                        Current Plan
                      </v-chip>
                    </h2>
                    <p class="text-h6 mt-2" style="color: #37474f">
                      Perfect for professional market researchers
                    </p>
                  </div>
                  <div class="text-right">
                    <div class="text-h3" style="color: #2ab3b8; font-weight: 700">
                      {{ getProfessionalPrice() }}
                      <span v-if="selectedCurrency === 'NZD'" class="text-caption">(GST Incl)</span>
                    </div>
                    <div class="text-body-1" style="color: #666">per month</div>
                  </div>
                </div>

                <!-- Features List -->
                <div class="features-list mb-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <div
                        class="feature-item mb-3"
                        v-for="feature in professionalFeatures.slice(0, 3)"
                        :key="feature"
                      >
                        <v-icon color="#2ab3b8" class="mr-2">mdi-check-circle</v-icon>
                        <span>{{ feature }}</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div
                        class="feature-item mb-3"
                        v-for="feature in professionalFeatures.slice(3)"
                        :key="feature"
                      >
                        <v-icon color="#2ab3b8" class="mr-2">mdi-check-circle</v-icon>
                        <span>{{ feature }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <v-radio-group v-model="selectedPlan" hide-details>
                  <v-radio
                    label="Select Professional Plan"
                    value="professional"
                    color="#2ab3b8"
                  ></v-radio>
                </v-radio-group>
              </v-card-text>
            </v-card>

            <!-- Order Summary and Checkout - Only for paid plans and when different from current -->
            <v-card
              v-if="selectedPlan && selectedPlan === 'professional' && selectedPlan !== currentPlan"
              class="checkout-card"
              style="border: 2px solid rgba(42, 179, 184, 0.3)"
            >
              <v-card-title class="pb-2">
                <h3 style="color: #2ab3b8">Complete Your Subscription</h3>
              </v-card-title>
              <v-card-text class="pa-6">
                <!-- Order Summary -->
                <v-card class="order-summary mb-4" outlined>
                  <v-card-text>
                    <h4 class="mb-3" style="color: #37474f">Order Summary</h4>
                    <div class="d-flex justify-space-between mb-2">
                      <span>{{ getSelectedPlanName() }} (Monthly)</span>
                      <span>{{ getSelectedPlanPrice() }}</span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex justify-space-between">
                      <strong>Starting at</strong>
                      <strong style="color: #2ab3b8">{{ getSelectedPlanPrice() }}/month</strong>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Billing Information -->
                <div class="billing-info mb-4">
                  <h4 class="mb-3" style="color: #37474f">Billing Information</h4>
                  <v-list class="pa-0">
                    <v-list-item class="px-0">
                      <v-list-item-icon>
                        <v-icon color="#2ab3b8">mdi-credit-card</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Secure payment processing by Stripe</v-list-item-title>
                        <v-list-item-subtitle
                          >Your payment information is encrypted and secure</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item class="px-0">
                      <v-list-item-icon>
                        <v-icon color="#2ab3b8">mdi-autorenew</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Monthly billing cycle</v-list-item-title>
                        <v-list-item-subtitle
                          >Cancel anytime from your account settings</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item class="px-0">
                      <v-list-item-icon>
                        <v-icon color="#2ab3b8">mdi-email-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Email receipts and notifications</v-list-item-title>
                        <v-list-item-subtitle
                          >Sent to
                          {{ currentUser?.email || "your email address" }}</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>

                <!-- Terms and Proceed Button -->
                <v-checkbox v-model="agreeToTerms" :rules="termsRules" required>
                  <template v-slot:label>
                    <span>
                      I agree to the
                      <a href="#/terms" style="color: #2ab3b8" target="_blank" @click.stop>
                        Terms of Service
                      </a>
                      and
                      <a href="#/privacy" style="color: #2ab3b8" target="_blank" @click.stop>
                        Privacy Policy
                      </a>
                    </span>
                  </template>
                </v-checkbox>

                <v-btn
                  color="#2ab3b8"
                  large
                  block
                  :disabled="!agreeToTerms || loading"
                  :loading="loading"
                  @click="proceedToStripe(selectedPlan)"
                  class="mt-4"
                >
                  Proceed to Secure Payment
                  <v-icon right size="24">mdi-shield-check</v-icon>
                </v-btn>

                <p class="text-center mt-3 text-caption" style="color: #666">
                  You'll be redirected to Stripe's secure payment page to complete your subscription
                </p>
              </v-card-text>
            </v-card>

            <!-- Free Plan Confirmation - Only when selecting Basic and not currently on Basic -->
            <v-card
              v-if="selectedPlan === 'basic' && currentPlan !== 'basic'"
              class="checkout-card"
              style="border: 2px solid rgba(42, 179, 184, 0.3)"
            >
              <v-card-title class="pb-2">
                <h3 style="color: #2ab3b8">Confirm Your Plan</h3>
              </v-card-title>
              <v-card-text class="pa-6">
                <div class="text-center mb-4">
                  <v-icon size="64" color="#2ab3b8" class="mb-4">mdi-check-circle</v-icon>
                  <h4 class="mb-3" style="color: #37474f">You've selected the Basic Plan</h4>
                  <p class="text-body-1" style="color: #666">
                    Start using Dynamic Insights with our free Basic plan. You can upgrade to
                    Professional anytime to unlock additional features.
                  </p>
                </div>

                <v-btn
                  color="#2ab3b8"
                  large
                  block
                  :loading="loading"
                  @click="selectFreePlan()"
                  class="mt-4"
                >
                  Confirm Basic Plan
                  <v-icon right size="24">mdi-check</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import APIService from "@/services/APIService";

export default {
  name: "SubscriptionPaymentView",
  data: () => ({
    currentUser: null,
    currentPlan: "basic",
    selectedPlan: "",
    selectedCurrency: "NZD",
    valid: false,
    loading: false,
    loadingUser: true,

    // Terms
    agreeToTerms: false,

    // Pricing data
    pricing: {},

    // Basic Plan Features
    basicFeatures: ["One Free project", "Up to 30 participants", "AI Data Analysis"],

    professionalFeatures: [
      "Up to 5 live projects",
      "Up to 500 participants per project",
      "AI Data Analysis",
      "Advanced Visualizations",
      "Export data in Excel format",
    ],

    // Validation Rules
    termsRules: [(v) => !!v || "You must agree to the terms"],
  }),

  async mounted() {
    await Promise.all([this.loadCurrentUser(), this.loadPricing()]);
  },

  methods: {
    async loadCurrentUser() {
      this.loadingUser = true;
      try {
        // Get user from existing Vuex store
        this.currentUser = this.$store.getters["tenancy/currentUser"];

        // Use the Vuex getters for plan determination
        if (this.$store.getters.hasActiveAccess) {
          this.currentPlan = "professional";

          // Detect currency from active subscription
          if (this.currentUser?.subscriptions) {
            const subscriptions = Object.values(this.currentUser.subscriptions);
            const activeSubscription = subscriptions.find(
              (sub) => sub.status === "active" || sub.status === "trialing"
            );

            if (activeSubscription?.price_id) {
              // Check against loaded pricing to determine currency
              if (this.pricing?.professional) {
                if (activeSubscription.price_id === this.pricing.professional.NZD?.price_id) {
                  this.selectedCurrency = "NZD";
                } else if (
                  activeSubscription.price_id === this.pricing.professional.USD?.price_id
                ) {
                  this.selectedCurrency = "USD";
                }
              }
            }
          }
        } else {
          this.currentPlan = "basic";
        }

        // Set selected plan to current plan initially
        this.selectedPlan = this.currentPlan;
      } catch (error) {
        console.error("Error loading user:", error);
        this.currentPlan = "basic";
        this.selectedPlan = "basic";
      } finally {
        this.loadingUser = false;
      }
    },

    async loadPricing() {
      try {
        const response = await APIService.getProductsWithPrices();
        const products = response.data.products;

        // Find professional product
        const professionalProduct = products.find((p) =>
          p.name.toLowerCase().includes("professional dynamic insights")
        );

        if (professionalProduct) {
          this.pricing.professional = {
            NZD: {
              amount: professionalProduct.prices.nzd.unit_amount / 100,
              price_id: professionalProduct.prices.nzd.price_id,
              symbol: "$",
            },
            USD: {
              amount: professionalProduct.prices.usd.unit_amount / 100,
              price_id: professionalProduct.prices.usd.price_id,
              symbol: "$",
            },
          };
        }
      } catch (error) {
        console.error("Error loading pricing:", error);
      }
    },

    selectPlan(plan) {
      this.selectedPlan = plan;
    },

    getProfessionalPrice() {
      const price = this.pricing.professional[this.selectedCurrency];
      return `${price.symbol}${price.amount}`;
    },

    getProfessionalPriceId() {
      return this.pricing.professional[this.selectedCurrency].price_id;
    },

    getSelectedPlanName() {
      if (this.selectedPlan === "basic") {
        return "Basic Dynamic Insights";
      } else if (this.selectedPlan === "professional") {
        return "Professional Dynamic Insights";
      }
      return "";
    },

    getSelectedPlanPrice() {
      if (this.selectedPlan === "basic") {
        return "Free";
      } else if (this.selectedPlan === "professional") {
        return this.getProfessionalPrice();
      }
      return "";
    },

    async selectFreePlan() {
      this.loading = true;

      try {
        const response = await APIService.cancelSubscription();

        if (response.data.success) {
          this.currentPlan = "basic";
          this.selectedPlan = "basic";
          await this.loadCurrentUser();
          alert(
            "You have been moved to the Basic (Free) plan! Your subscription has been cancelled."
          );
        } else {
          throw new Error("Failed to cancel subscription");
        }
      } catch (error) {
        console.error("Failed to cancel subscription:", error);
        alert("Failed to cancel your subscription. Please try again or contact support.");
      } finally {
        this.loading = false;
      }
    },

    async proceedToStripe() {
      this.loading = true;

      try {
        // Use the correct price ID based on selected currency
        const actualPriceId = this.getProfessionalPriceId();

        // Call your API to create Stripe Checkout Session
        const response = await APIService.createCheckoutSession(actualPriceId);

        // Redirect to Stripe Checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
      } catch (error) {
        console.error("Failed to create checkout session:", error);
        alert("Failed to initiate payment. Please try again.");
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.subscription-wrapper {
  background: linear-gradient(to bottom, #ffffff, #f5f5f5);
  min-height: 100vh;
}

.hero-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.subscription-card {
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 25px rgba(42, 179, 184, 0.1);
  cursor: pointer;
}

.subscription-card:hover {
  transform: translateY(-4px);
}

.selected-plan {
  border-color: #2ab3b8 !important;
  box-shadow: 0 6px 30px rgba(42, 179, 184, 0.2) !important;
}

.customer-info-card,
.checkout-card {
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 25px rgba(42, 179, 184, 0.1);
}

.feature-item {
  display: flex;
  align-items: center;
}

.order-summary {
  background-color: #f8f9fa;
}

.billing-info .v-list-item {
  min-height: 48px;
}

@media (max-width: 960px) {
  .text-h2 {
    font-size: 2.5rem !important;
  }

  .text-h4 {
    font-size: 1.75rem !important;
  }

  .text-h5 {
    font-size: 1.25rem !important;
  }
}
</style>
